import type { translations, LanguageCode } from '@/translations/translations';
import type { AuthUser } from '@/types/auth.types';
import { isAdmin, isModerator, isAccountant, isCollector, resolveAdminLabel } from '../navigation-registry';
import { canAccessContributorWorkspace, normalizeUserRole } from '@/lib/auth/roles';
import type { MenuLink } from './user-menu.types';

export function buildPrimaryLinks(lang: string, t: (typeof translations)[LanguageCode]): MenuLink[] {
  const base = `/${lang}`;
  return [
    { href: `${base}/settings`, label: t.myProfile, eyebrow: t.accountEyebrow, icon: 'user' },
    { href: `${base}/leaderboard`, label: t.leaderboard, eyebrow: t.leaderboardEyebrow, icon: 'target', tone: 'orange' },
    { href: `${base}/my-contributions`, label: t.myContributions, eyebrow: t.activityEyebrow, icon: 'star', tone: 'primary' },
  ];
}

export function buildRoleLinks(lang: string, t: (typeof translations)[LanguageCode], user: AuthUser): MenuLink[] {
  const base = `/${lang}`;
  const links: MenuLink[] = [];

  if (canAccessContributorWorkspace(user.role)) {
    links.push({
      href: `${base}/get-paid`,
      label: t.getPaidPage.workspaceTitle,
      eyebrow: t.contributor,
      icon: 'wallet',
      tone: 'emerald',
    });
  }

  if (isCollector(user)) {
    links.push({ href: `${base}/collector`, label: t.collectorSpaceLabel, eyebrow: t.terrainEyebrow, icon: 'users', tone: 'emerald' });
  }
  if (isModerator(user)) {
    links.push({ href: `${base}/moderator`, label: t.moderatorPanelLabel, eyebrow: t.qualityEyebrow, icon: 'checkCircle', tone: 'primary' });
  }
  if (isAccountant(user)) {
    links.push({ href: `${base}/accountant/dashboard`, label: t.accountantDashboardLabel, eyebrow: t.financeEyebrow, icon: 'wallet', tone: 'emerald' });
  }
  if (isAdmin(user)) {
    links.push({
      href: `${base}/admin/dashboard`,
      label: resolveAdminLabel(user, t.admin.dashboard.title, t.admin.dashboard.superAdminView),
      eyebrow: t.pilotageEyebrow,
      icon: 'shield',
      tone: 'orange',
    });
  }

  return links;
}

export function getRoleCaption(user: AuthUser, t: (typeof translations)[LanguageCode]): string {
  const role = normalizeUserRole(user.role);
  if (role === 'user') return t.contributor;
  return t.admin.layout.roles[role];
}

export function getInitials(user: AuthUser): string {
  return (user.name || user.username || '?').charAt(0).toUpperCase();
}

export function getDisplayName(user: AuthUser): string {
  return user.name || user.username;
}
